import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import './styles/brand.css';
import { isLegacyHost } from './lib/host';

// Host-aware document title (one bundle, two hosts):
// "Rainmaker" on app.rainmkr.co.za, "Legacy (3Wi PMO)" on legacy.rainmkr.co.za.
document.title = isLegacyHost() ? 'Legacy (3Wi PMO)' : 'Rainmaker';

// Stale-chunk recovery.
// Routes are lazy-loaded, so each page is a hash-named chunk (e.g. SalesInvoices-DzW2O2SK.js).
// After a deploy those hashes change and the old files stop existing — so any tab that was open
// across the deploy will 404 the moment the user navigates to a not-yet-loaded route
// ("Failed to fetch dynamically imported module"). The app isn't broken; the tab is just stale.
// Vite fires `vite:preloadError` for exactly this. Reload once to pick up the new index.html.
// The sessionStorage guard stops a genuinely-missing chunk becoming an infinite reload loop.
const RELOAD_FLAG = '3wi.chunk-reloaded';
window.addEventListener('vite:preloadError', (e) => {
  if (sessionStorage.getItem(RELOAD_FLAG)) return; // already tried — let the ErrorBoundary show it
  e.preventDefault();
  sessionStorage.setItem(RELOAD_FLAG, '1');
  window.location.reload();
});
// A navigation that loads cleanly means we're on the current build again.
window.addEventListener('load', () => {
  window.setTimeout(() => sessionStorage.removeItem(RELOAD_FLAG), 5_000);
});

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30_000 } } });

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={qc}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

// 🔴 REGISTERED LAST, AND ONLY IN A REAL BUILD. The worker exists so the app OPENS on a phone with
// no signal — Ravi turned data off in the field on 25 Sep and got Chrome's offline page, not ours.
// Its caching strategy is network-first for the shell, which is deliberately STRICTER than the
// browser default that served him a fortnight-old bundle that same morning. See public/sw.js.
// Failure is silent on purpose: a browser that refuses to register one must still run the app.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* the app works without it */ });
  });
}
